import React, { Component } from "react";
import firebase from "firebase";
import DB_PATH from "../../../../../../database/DB_PATH";
import { Author } from "../../../../../../types/Author";
import { SlideContent, SlideImageOption, UnsplashImage } from "./types";
import {
  SlideGeneratorProps,
  SlideGeneratorState,
  SlideAuthorReference,
  SlideTypeDefinition,
  StoryPromptTemplateMap,
  StoryType,
} from "./SlideGenerator.types";
import {
  generateSlideTitles,
  pickStoryFormat,
  extractImageKeyword,
  generateImagePrompt,
  generateGeminiImage,
  generateGptImage,
  searchUnsplashImages,
  buildOptionsFromImages,
  getDefaultStoryPromptTemplates,
} from "./SlideGenerator.api";
import { renderSlideWithTextOverlay } from "./SlideGenerator.canvasUtils";
import {
  getCommunitySlideSettings,
  saveCommunitySlideSettings,
} from "../../../../../../database/community/communityMedia";
import { getCommunityMediaConfig } from "../../../../../../database/community/mediaConfig";
import { SlideGeneratorStudioView } from "./SlideGenerator.studio.view";
import { uploadMedia } from "../../../../../../database/media/cloudflareMedia";

// Built-in defaults have been retired — all story type definitions now come
// from mediaConfig.customFormats (research-discovered). Legacy slide-settings
// custom story types are also pulled in for backward compat.
const getLegacyCustomStoryTypeDefinitions = (
  storedDefinitions?: unknown
): SlideTypeDefinition[] => {
  if (!Array.isArray(storedDefinitions)) return [];

  return storedDefinitions
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const rawType = typeof (item as { type?: unknown }).type === "string" ? (item as { type: string }).type.trim() : "";
      const rawLabel = typeof (item as { label?: unknown }).label === "string" ? (item as { label: string }).label.trim() : "";
      if (!rawType || !rawLabel) return null;

      const rawDescription = typeof (item as { description?: unknown }).description === "string"
        ? (item as { description: string }).description.trim()
        : "";
      const rawExample = typeof (item as { example?: unknown }).example === "string"
        ? (item as { example: string }).example.trim()
        : "";
      const rawIcon = typeof (item as { icon?: unknown }).icon === "string"
        ? (item as { icon: string }).icon.trim()
        : "";

      return {
        type: rawType,
        label: rawLabel,
        description: rawDescription || "Legacy custom story format.",
        example: rawExample || `"${rawLabel}"`,
        icon: rawIcon || "✨",
        isCustom: true,
      } satisfies SlideTypeDefinition;
    })
    .filter(Boolean) as SlideTypeDefinition[];
};

const buildCustomStoryTypeKey = (
  label: string,
  storyTypeDefinitions: SlideTypeDefinition[]
): StoryType => {
  const base = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "custom-slide-type";

  const existing = new Set(storyTypeDefinitions.map((definition) => definition.type));
  let nextKey = base;
  let suffix = 2;
  while (existing.has(nextKey)) {
    nextKey = `${base}-${suffix}`;
    suffix += 1;
  }
  return nextKey;
};

const getCustomStoryTypeDefinitions = (
  storyTypeDefinitions: SlideTypeDefinition[]
): SlideTypeDefinition[] => storyTypeDefinitions.filter((definition) => definition.isCustom);

class SlideGenerator extends Component<SlideGeneratorProps, SlideGeneratorState> {
  state: SlideGeneratorState = {
    isGenerating: false,
    progress: "",
    progressPercent: 0,
    slides: [],
    currentStep: "idle",
    promptInputs: [],
    aiGeneratingIndex: null,
    aiGeneratingAll: false,
    aiGeneratingAllProgress: "",
    isSaving: false,
    isDownloading: false,
    renderedSlides: [],
    imageProvider: "gemini",
    globalTheme: "",
    writingStyle: "",
    authors: [],
    isLoadingAuthors: false,
    selectedAuthorId: "",
    selectedAuthorName: "",
    selectedAuthorProfilePhotoURL: "",
    selectedAuthorReferenceImage: "",
    isLoadingAuthorReference: false,
    communityTitle: "",
    communityDescription: "",
    communityShortDescription: "",
    communityCategoryName: "",
    communityTags: [],
    globalReferenceImages: [],
    storyType: "auto",
    autoPickedStoryType: null,
    autoPickReason: null,
    isPickingStoryFormat: false,
    buyerPersona: "",
    editableStepText: "",
    isSavingStepText: false,
    stepTextSaveStatus: "",
    storyTypeDefinitions: [],
    storyPromptTemplates: getDefaultStoryPromptTemplates(),
    selectedPromptStoryType: "auto",
    isSavingStoryPrompts: false,
    storyPromptSaveStatus: "",
    isAddingStoryType: false,
    newStoryTypeLabel: "",
    newStoryTypePrompt: "",
  };

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  componentDidMount() {
    if (this.props.existingSlides?.length) {
      this.setState({ slides: this.props.existingSlides, currentStep: "complete" });
    }
    this.syncEditableStepTextFromProps();
    this.loadCommunityStyles();
    this.loadAuthorsAndSelection();
  }

  componentDidUpdate(prevProps: SlideGeneratorProps) {
    if (prevProps.existingSlides !== this.props.existingSlides && this.props.existingSlides?.length) {
      this.setState({ slides: this.props.existingSlides, currentStep: "complete" });
    }
    if (
      prevProps.communityId !== this.props.communityId ||
      prevProps.stepId !== this.props.stepId
    ) {
      this.loadCommunityStyles();
      this.loadAuthorsAndSelection();
    }
    if (
      prevProps.stepId !== this.props.stepId ||
      prevProps.text !== this.props.text
    ) {
      this.syncEditableStepTextFromProps();
    }
  }

  // ─── Data Loading ──────────────────────────────────────────────────────────

  syncEditableStepTextFromProps = () => {
    this.setState({
      editableStepText: this.props.text || "",
      stepTextSaveStatus: "",
      isSavingStepText: false,
    });
  };

  loadCommunityStyles = async () => {
    const { communityId } = this.props;
    if (!communityId) {
      this.setState({
        writingStyle: "",
        globalTheme: "",
        buyerPersona: "",
        voiceOfCustomer: undefined,
        formatProfile: undefined,
        customFormats: undefined,
        communityTitle: "",
        communityDescription: "",
        communityShortDescription: "",
        communityCategoryName: "",
        communityTags: [],
        storyTypeDefinitions: [],
        storyPromptTemplates: getDefaultStoryPromptTemplates(),
        storyPromptSaveStatus: "",
        isAddingStoryType: false,
        newStoryTypeLabel: "",
        newStoryTypePrompt: "",
      });
      return;
    }
    try {
      const communitySnap = await firebase.firestore().collection(DB_PATH.COMMUNITY).doc(communityId).get();
      const data = communitySnap.data();
      const [slideSettings, mediaConfig] = await Promise.all([
        getCommunitySlideSettings(communityId, { communityData: data }),
        getCommunityMediaConfig(communityId, { communityData: data }),
      ]);
      const baseStoryTypeDefinitions = getLegacyCustomStoryTypeDefinitions(slideSettings.slideStoryTypes);
      // Inject research-discovered custom formats into the picker.
      const customFormatDefinitions = (mediaConfig.customFormats || []).map(
        (cf) => ({
          type: cf.key,
          icon: cf.icon || "🆕",
          label: cf.label,
          description: cf.lever
            ? `Discovered format · lever: ${cf.lever}`
            : "Discovered format from audience research.",
          example: cf.evidence ? `"${cf.evidence}"` : `"${cf.label}"`,
          isCustom: true as const,
        })
      );
      const customKeys = new Set(customFormatDefinitions.map((d) => d.type));
      const storyTypeDefinitions = [
        ...baseStoryTypeDefinitions.filter((d) => !customKeys.has(d.type as string)),
        ...customFormatDefinitions,
      ];
      // Prefer the research-derived visualStyleProfile when available.
      // Fall back to legacy free-form visualStyle on the community/mediaConfig
      // doc for backward compat.
      const visualStyleProfile = mediaConfig.visualStyleProfile;
      const visualStyleFromProfile = visualStyleProfile
        ? [
            visualStyleProfile.description?.trim(),
            visualStyleProfile.tags?.length
              ? `Style cues: ${visualStyleProfile.tags.join(", ")}.`
              : "",
          ]
            .filter(Boolean)
            .join(" ")
        : "";
      const slideVisualStyle =
        visualStyleFromProfile ||
        mediaConfig.visualStyle ||
        (typeof data?.slideVisualStyle === "string"
          ? data.slideVisualStyle
          : data?.visualStyle || "");
      // Default to "auto" — the classifier picks a real format at generate
      // time from the community's research-derived custom formats. If the
      // user previously locked a specific format that no longer exists,
      // fall back to auto.
      const currentStoryType = this.state.storyType;
      const nextStoryType =
        currentStoryType === "auto" ||
        storyTypeDefinitions.some((definition) => definition.type === currentStoryType)
          ? currentStoryType
          : "auto";
      const nextPromptStoryType =
        storyTypeDefinitions.some((definition) => definition.type === this.state.selectedPromptStoryType)
          ? this.state.selectedPromptStoryType
          : nextStoryType;
      this.setState({
        writingStyle: mediaConfig.writingStyle || data?.writingStyle || "",
        globalTheme: slideVisualStyle,
        buyerPersona: mediaConfig.buyerPersona || data?.buyerPersona || "",
        voiceOfCustomer: mediaConfig.voiceOfCustomer,
        formatProfile: mediaConfig.formatProfile,
        customFormats: mediaConfig.customFormats,
        communityTitle: data?.title || "",
        communityDescription: data?.description || "",
        communityShortDescription: data?.shortDescription || "",
        communityCategoryName: data?.categoryName || "",
        communityTags: Array.isArray(data?.tags) ? data.tags : [],
        storyType: nextStoryType,
        storyTypeDefinitions,
        // Story prompt templates are deprecated as a coach-editable surface.
        // The runtime always uses defaults — custom story TYPES (e.g. "My
        // Brand Take") still work because they add new angle options, but
        // their prompts come from the built-in defaults.
        storyPromptTemplates: getDefaultStoryPromptTemplates(),
        selectedPromptStoryType: nextPromptStoryType,
        storyPromptSaveStatus: "",
        isAddingStoryType: false,
        newStoryTypeLabel: "",
        newStoryTypePrompt: "",
      });
    } catch (e) {
      console.error("Error loading community styles:", e);
    }
  };

  loadCommunityName = async (): Promise<string> => {
    const { communityId } = this.props;
    if (!communityId) return "Tribed";
    try {
      const snap = await firebase.firestore().collection(DB_PATH.COMMUNITY).doc(communityId).get();
      return snap.data()?.title || "Tribed";
    } catch (e) {
      console.error("Error loading community name:", e);
      return "Tribed";
    }
  };

  blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  resizeImageToDataUrl = (blob: Blob, maxSize = 512): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to resize image")); };
      img.src = url;
    });

  convertImageUrlToDataUrl = async (imageUrl: string): Promise<string> => {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("data:")) return imageUrl;

    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error("Failed to fetch author profile image");

    return this.resizeImageToDataUrl(await response.blob());
  };

  applySelectedAuthorReference = async (reference: SlideAuthorReference | null) => {
    const nextAuthorId = reference?.id || "";
    const nextAuthorName = reference?.name || "";
    const nextAuthorProfilePhotoURL = reference?.profilePhotoURL || "";

    this.setState({
      selectedAuthorId: nextAuthorId,
      selectedAuthorName: nextAuthorName,
      selectedAuthorProfilePhotoURL: nextAuthorProfilePhotoURL,
      selectedAuthorReferenceImage: "",
      isLoadingAuthorReference: !!nextAuthorProfilePhotoURL,
    });

    if (!nextAuthorProfilePhotoURL) {
      this.setState({ isLoadingAuthorReference: false });
      return;
    }

    try {
      const dataUrl = await this.convertImageUrlToDataUrl(nextAuthorProfilePhotoURL);
      this.setState((prev) =>
        prev.selectedAuthorId === nextAuthorId &&
        prev.selectedAuthorProfilePhotoURL === nextAuthorProfilePhotoURL
          ? { selectedAuthorReferenceImage: dataUrl, isLoadingAuthorReference: false }
          : null
      );
    } catch (error) {
      console.error("Error loading selected author profile image:", error);
      this.setState((prev) =>
        prev.selectedAuthorId === nextAuthorId &&
        prev.selectedAuthorProfilePhotoURL === nextAuthorProfilePhotoURL
          ? { selectedAuthorReferenceImage: "", isLoadingAuthorReference: false }
          : null
      );
    }
  };

  loadAuthorsAndSelection = async () => {
    const { communityId, stepId } = this.props;

    if (!communityId) {
      this.setState({
        authors: [],
        isLoadingAuthors: false,
        selectedAuthorId: "",
        selectedAuthorName: "",
        selectedAuthorProfilePhotoURL: "",
        selectedAuthorReferenceImage: "",
        isLoadingAuthorReference: false,
      });
      return;
    }

    this.setState({ isLoadingAuthors: true });

    try {
      const db = firebase.firestore();
      const [authorsSnapshot, stepSnapshot] = await Promise.all([
        db.collection(DB_PATH.AUTHOR).where("communityId", "==", communityId).get(),
        stepId ? db.collection(DB_PATH.PLAN_STEP).doc(stepId).get() : Promise.resolve(null),
      ]);

      const authors = authorsSnapshot.docs
        .map((doc) => doc.data() as Author)
        .sort(
          (a, b) =>
            (a.position ?? Number.MAX_SAFE_INTEGER) -
            (b.position ?? Number.MAX_SAFE_INTEGER)
        );

      const stepData = stepSnapshot?.data?.() || null;
      const savedReference = (stepData?.slideAuthorReference || null) as
        | SlideAuthorReference
        | null;
      const matchedAuthor = savedReference
        ? authors.find((author) => author.id === savedReference.id) || null
        : null;

      const initialReference = savedReference
        ? {
            id: savedReference.id,
            name: savedReference.name || matchedAuthor?.fullName || "",
            profilePhotoURL:
              savedReference.profilePhotoURL || matchedAuthor?.profilePhotoURL || "",
          }
        : null;

      this.setState({ authors, isLoadingAuthors: false });
      await this.applySelectedAuthorReference(initialReference);
    } catch (error) {
      console.error("Error loading authors for slide generator:", error);
      this.setState({
        authors: [],
        isLoadingAuthors: false,
        selectedAuthorId: "",
        selectedAuthorName: "",
        selectedAuthorProfilePhotoURL: "",
        selectedAuthorReferenceImage: "",
        isLoadingAuthorReference: false,
      });
    }
  };

  ensureSelectedAuthorReferenceImage = async (): Promise<string> => {
    const {
      selectedAuthorId,
      selectedAuthorProfilePhotoURL,
      selectedAuthorReferenceImage,
    } = this.state;

    if (selectedAuthorReferenceImage || !selectedAuthorProfilePhotoURL) {
      return selectedAuthorReferenceImage;
    }

    this.setState({ isLoadingAuthorReference: true });

    try {
      const dataUrl = await this.convertImageUrlToDataUrl(selectedAuthorProfilePhotoURL);
      this.setState((prev) =>
        prev.selectedAuthorId === selectedAuthorId &&
        prev.selectedAuthorProfilePhotoURL === selectedAuthorProfilePhotoURL
          ? { selectedAuthorReferenceImage: dataUrl, isLoadingAuthorReference: false }
          : null
      );
      return dataUrl;
    } catch (error) {
      console.error("Error preparing author reference image:", error);
      this.setState((prev) =>
        prev.selectedAuthorId === selectedAuthorId
          ? { selectedAuthorReferenceImage: "", isLoadingAuthorReference: false }
          : null
      );
      return "";
    }
  };

  handleAuthorSelectionChange = async (authorId: string) => {
    const nextAuthor = this.state.authors.find((author) => author.id === authorId) || null;
    await this.applySelectedAuthorReference(
      nextAuthor
        ? {
            id: nextAuthor.id,
            name: nextAuthor.fullName,
            profilePhotoURL: nextAuthor.profilePhotoURL || "",
          }
        : null
    );
  };

  // ─── AI Image Dispatch ─────────────────────────────────────────────────────

  generateAIImage = async (prompt: string, perSlideRefs?: string[]): Promise<string> => {
    const { geminiApiKey } = this.props;
    const { globalReferenceImages, imageProvider } = this.state;
    const selectedAuthorReferenceImage = await this.ensureSelectedAuthorReferenceImage();
    const styleRefs = [
      ...(globalReferenceImages || []),
      ...(perSlideRefs || []),
    ];

    return imageProvider === "gpt"
      ? generateGptImage(
          prompt,
          styleRefs.length ? styleRefs : undefined,
          selectedAuthorReferenceImage || undefined
        )
      : generateGeminiImage(
          geminiApiKey,
          prompt,
          styleRefs.length ? styleRefs : undefined,
          selectedAuthorReferenceImage || undefined
        );
  };

  handleAddGlobalReference = (file: File | null) => {
    if (!file) return;
    this.resizeImageToDataUrl(file).then((dataUrl) => {
      this.setState((prev) => ({
        globalReferenceImages: [...prev.globalReferenceImages, dataUrl],
      }));
    });
  };

  handleRemoveGlobalReference = (idx: number) =>
    this.setState((prev) => {
      const refs = [...prev.globalReferenceImages];
      refs.splice(idx, 1);
      return { globalReferenceImages: refs };
    });

  // ─── Slide Generation ──────────────────────────────────────────────────────

  generateSlides = async () => {
    const { unsplashAccessKey } = this.props;
    const {
      editableStepText,
      writingStyle,
      globalTheme,
      storyType,
      buyerPersona,
      selectedAuthorProfilePhotoURL,
      communityTitle,
      communityDescription,
      communityShortDescription,
      communityCategoryName,
      communityTags,
      storyPromptTemplates,
    } = this.state;

    const text = editableStepText.trim();

    if (!text) { alert("Please add some text content first."); return; }

    const sharedContext = {
      title: communityTitle,
      description: communityDescription,
      shortDescription: communityShortDescription,
      categoryName: communityCategoryName,
      tags: communityTags,
      buyerPersona,
      storyPromptTemplates,
      voiceOfCustomer: this.state.voiceOfCustomer,
      formatProfile: this.state.formatProfile,
      customFormats: this.state.customFormats,
    };

    // If story format is set to "auto", classify first.
    let effectiveStoryType: StoryType = storyType;
    if (storyType === "auto") {
      this.setState({
        isPickingStoryFormat: true,
        autoPickedStoryType: null,
        autoPickReason: null,
        isGenerating: true,
        progress: "Picking the best story format for this lesson...",
        progressPercent: 5,
        slides: [],
        currentStep: "generating",
      });
      try {
        const picked = await pickStoryFormat(text, sharedContext);
        effectiveStoryType = picked.format;
        this.setState({
          autoPickedStoryType: picked.format,
          autoPickReason: picked.reason,
          isPickingStoryFormat: false,
        });
      } catch (err: any) {
        console.error("Auto-pick failed:", err);
        this.setState({
          isGenerating: false,
          isPickingStoryFormat: false,
          autoPickedStoryType: null,
          autoPickReason: null,
          currentStep: "idle",
          progress:
            err?.message ||
            "No formats yet. Run 🔎 Find audience language and 🔎 Find top formats from the inspector before generating.",
        });
        return;
      }
    } else {
      this.setState({
        autoPickedStoryType: null,
        autoPickReason: null,
      });
    }

    this.setState({ isGenerating: true, progress: "Generating hook-grabbing slide titles...", progressPercent: 10, slides: [], currentStep: "generating" });

    try {
      const titles = await generateSlideTitles(text, writingStyle, effectiveStoryType, sharedContext);
      if (!titles.length) {
        this.setState({ progress: "Failed to generate slide titles. Please try again.", currentStep: "idle", isGenerating: false });
        return;
      }

      this.setState({ progress: `Generated ${titles.length} slides. Preparing image prompts...`, progressPercent: 30 });

      const activeFormat = (this.state.customFormats || []).find(
        (f) => f.key === effectiveStoryType
      );
      const formatBrief = activeFormat?.structure || "";

      const slides: SlideContent[] = [];
      for (let i = 0; i < titles.length; i++) {
        this.setState({ progress: `Preparing slide ${i + 1}/${titles.length}...`, progressPercent: 30 + Math.floor((i / titles.length) * 60) });

        const [keyword, imagePrompt] = await Promise.all([
          extractImageKeyword(titles[i]),
          generateImagePrompt(titles[i], globalTheme, writingStyle, effectiveStoryType, !!selectedAuthorProfilePhotoURL, formatBrief),
        ]);

        let images: UnsplashImage[] = await searchUnsplashImages(unsplashAccessKey, keyword);
        if (!images.length) images = await searchUnsplashImages(unsplashAccessKey, "motivation inspiration");

        slides.push({ title: titles[i], subtitle: "", keyword, imagePrompt, options: buildOptionsFromImages(images), selectedIndex: 0 });
      }

      this.setState({ slides, promptInputs: slides.map((s) => s.imagePrompt), progress: "Review and customize your slides", progressPercent: 100, currentStep: slides.length ? "selecting" : "idle", isGenerating: false });
    } catch (e) {
      console.error("Error generating slides:", e);
      this.setState({ progress: "Error generating slides. Please try again.", currentStep: "idle", isGenerating: false });
    }
  };

  // ─── Per-Slide Handlers ────────────────────────────────────────────────────

  handleGenerateAIForSlide = async (idx: number) => {
    const { slides, promptInputs } = this.state;
    const slide = slides[idx];
    const prompt = (promptInputs[idx] || slide?.imagePrompt || "").trim();
    if (!slide || !prompt) return;

    this.setState({ aiGeneratingIndex: idx });
    try {
      const imageUrl = await this.generateAIImage(prompt, slide.referenceImages);
      const option: SlideImageOption = { imageUrl, thumbnailUrl: imageUrl, alt: "AI Generated", photographer: this.state.imageProvider === "gpt" ? "GPT Image" : "Gemini", isAiGenerated: true };
      this.setState((prev) => {
        const next = [...prev.slides];
        if (!next[idx]) return prev;
        next[idx] = { ...next[idx], imagePrompt: prompt, options: [option, ...next[idx].options], selectedIndex: 0 };
        return { ...prev, slides: next };
      });
    } catch (e) {
      console.error("Error generating AI image:", e);
      alert((e as Error)?.message || "Error generating AI image. Try again.");
    } finally {
      this.setState({ aiGeneratingIndex: null });
    }
  };

  handleGenerateAllAIImages = async () => {
    const { slides, promptInputs } = this.state;
    if (!slides.length) return;
    const remainingIndexes = slides.reduce<number[]>((indexes, slide, index) => {
      const hasPrompt = (promptInputs[index] || slide?.imagePrompt || "").trim();
      const selectedOption = slide?.options?.[slide?.selectedIndex ?? 0];
      const alreadyAiGenerated = selectedOption?.isAiGenerated === true;
      if (hasPrompt && !alreadyAiGenerated) indexes.push(index);
      return indexes;
    }, []);
    if (!remainingIndexes.length) {
      alert("Remaining slides are missing image prompts.");
      return;
    }
    const totalRemaining = remainingIndexes.length;

    this.setState({ aiGeneratingAll: true, aiGeneratingAllProgress: `Generating image 1 of ${totalRemaining}...` });

    for (let batchIndex = 0; batchIndex < remainingIndexes.length; batchIndex++) {
      const i = remainingIndexes[batchIndex];
      const prompt = (promptInputs[i] || slides[i].imagePrompt || "").trim();
      if (!prompt) continue;
      this.setState({ aiGeneratingIndex: i, aiGeneratingAllProgress: `Generating image ${batchIndex + 1} of ${totalRemaining}...` });
      try {
        const imageUrl = await this.generateAIImage(prompt, slides[i].referenceImages);
        const option: SlideImageOption = { imageUrl, thumbnailUrl: imageUrl, alt: "AI Generated", photographer: this.state.imageProvider === "gpt" ? "GPT Image" : "Gemini", isAiGenerated: true };
        this.setState((prev) => {
          const next = [...prev.slides];
          if (!next[i]) return prev;
          next[i] = { ...next[i], imagePrompt: prompt, options: [option, ...next[i].options], selectedIndex: 0 };
          return { ...prev, slides: next };
        });
      } catch (e) {
        console.error(`Error generating AI image for slide ${i + 1}:`, e);
      }
    }
    this.setState({ aiGeneratingAll: false, aiGeneratingAllProgress: "", aiGeneratingIndex: null });
  };

  handleSearchForSlide = async (idx: number) => {
    const query = (this.state.promptInputs[idx] || "").trim();
    if (!query) return;
    this.setState({ aiGeneratingIndex: idx });
    try {
      let images = await searchUnsplashImages(this.props.unsplashAccessKey, query);
      if (!images.length) images = await searchUnsplashImages(this.props.unsplashAccessKey, "inspiration");
      if (!images.length) { this.setState({ progress: "No images found. Try a different search." }); return; }
      this.setState((prev) => {
        const next = [...prev.slides];
        if (!next[idx]) return prev;
        next[idx] = { ...next[idx], keyword: query, options: buildOptionsFromImages(images), selectedIndex: 0 };
        return { ...prev, slides: next };
      });
    } catch (e) {
      console.error("Error searching images:", e);
      this.setState({ progress: "Error searching images. Try again." });
    } finally {
      this.setState({ aiGeneratingIndex: null });
    }
  };

  handleSelectOption = (slideIdx: number, optIdx: number) =>
    this.setState((prev) => {
      const next = [...prev.slides];
      if (!next[slideIdx]) return prev;
      next[slideIdx] = { ...next[slideIdx], selectedIndex: optIdx };
      return { ...prev, slides: next };
    });

  handlePromptInputChange = (slideIdx: number, value: string) =>
    this.setState((prev) => { const next = [...prev.promptInputs]; next[slideIdx] = value; return { ...prev, promptInputs: next }; });

  handleTitleChange = (slideIdx: number, title: string) =>
    this.setState((prev) => {
      const next = [...prev.slides];
      if (!next[slideIdx]) return prev;
      next[slideIdx] = { ...next[slideIdx], title };
      return { ...prev, slides: next };
    });

  handleSubtitleChange = (slideIdx: number, subtitle: string) =>
    this.setState((prev) => {
      const next = [...prev.slides];
      if (!next[slideIdx]) return prev;
      next[slideIdx] = { ...next[slideIdx], subtitle };
      return { ...prev, slides: next };
    });

  handleUploadForSlide = (slideIdx: number, file: File | null) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const option: SlideImageOption = { imageUrl: url, thumbnailUrl: url, alt: file.name, photographer: "Uploaded", isUploaded: true };
    this.setState((prev) => {
      const next = [...prev.slides];
      if (!next[slideIdx]) return prev;
      next[slideIdx] = { ...next[slideIdx], options: [option, ...next[slideIdx].options], selectedIndex: 0 };
      return { ...prev, slides: next };
    });
  };

  handleAddReferenceImage = (slideIdx: number, file: File | null) => {
    if (!file) return;
    this.resizeImageToDataUrl(file).then((dataUrl) => {
      this.setState((prev) => {
        const next = [...prev.slides];
        if (!next[slideIdx]) return prev;
        next[slideIdx] = { ...next[slideIdx], referenceImages: [...(next[slideIdx].referenceImages || []), dataUrl] };
        return { ...prev, slides: next };
      });
    });
  };

  handleRemoveReferenceImage = (slideIdx: number, refIdx: number) =>
    this.setState((prev) => {
      const next = [...prev.slides];
      if (!next[slideIdx]) return prev;
      const refs = [...(next[slideIdx].referenceImages || [])];
      refs.splice(refIdx, 1);
      next[slideIdx] = { ...next[slideIdx], referenceImages: refs };
      return { ...prev, slides: next };
    });

  handlePromptEditorStoryTypeChange = (storyType: StoryType) =>
    this.setState({ selectedPromptStoryType: storyType, storyPromptSaveStatus: "" });

  handleStepTextChange = (value: string) =>
    this.setState({ editableStepText: value, stepTextSaveStatus: "" });

  saveStepText = async () => {
    const { stepId, setFieldValue } = this.props;
    const { editableStepText } = this.state;

    if (!stepId) {
      this.setState({ stepTextSaveStatus: "Step text cannot be saved without a step id." });
      return;
    }

    this.setState({ isSavingStepText: true, stepTextSaveStatus: "Saving step text..." });
    try {
      await firebase
        .firestore()
        .collection(DB_PATH.PLAN_STEP)
        .doc(stepId)
        .set({ text: editableStepText || "" }, { merge: true });

      setFieldValue?.("text", editableStepText || "");
      this.setState({
        isSavingStepText: false,
        stepTextSaveStatus: "Step text saved.",
      });
    } catch (error) {
      console.error("Error saving step text:", error);
      this.setState({
        isSavingStepText: false,
        stepTextSaveStatus: "Error saving step text. Please try again.",
      });
    }
  };

  handleOpenNewStoryTypeForm = () =>
    this.setState({ isAddingStoryType: true, storyPromptSaveStatus: "" });

  handleCloseNewStoryTypeForm = () =>
    this.setState({ isAddingStoryType: false, storyPromptSaveStatus: "" });

  handleStoryPromptTemplateChange = (storyType: StoryType, value: string) =>
    this.setState((prev) => ({
      storyPromptTemplates: {
        ...prev.storyPromptTemplates,
        [storyType]: value,
      },
      storyPromptSaveStatus: "",
    }));

  handleResetStoryPromptTemplate = (storyType: StoryType) =>
    this.setState((prev) => {
      const next = { ...prev.storyPromptTemplates };
      // Built-in defaults retired — reset removes any custom override so the
      // runtime falls back to the research-discovered structure.
      delete next[storyType];
      return { storyPromptTemplates: next, storyPromptSaveStatus: "" };
    });

  persistStoryPromptConfig = async (
    storyTypeDefinitions: SlideTypeDefinition[] = this.state.storyTypeDefinitions,
    storyPromptTemplates: StoryPromptTemplateMap = this.state.storyPromptTemplates,
    successMessage = "Prompt templates saved for this community."
  ) => {
    const { communityId } = this.props;

    if (!communityId) {
      this.setState({ storyPromptSaveStatus: "Save unavailable until this step belongs to a community." });
      return false;
    }

    this.setState({ isSavingStoryPrompts: true, storyPromptSaveStatus: "Saving prompt templates..." });
    try {
      await saveCommunitySlideSettings(communityId, {
        slideStoryTypes: getCustomStoryTypeDefinitions(storyTypeDefinitions),
        slideStoryPromptTemplates: storyPromptTemplates,
      });

      this.setState({
        isSavingStoryPrompts: false,
        storyPromptSaveStatus: successMessage,
      });
      return true;
    } catch (error) {
      console.error("Error saving story prompt templates:", error);
      this.setState({
        isSavingStoryPrompts: false,
        storyPromptSaveStatus: "Error saving prompt templates. Please try again.",
      });
      return false;
    }
  };

  saveStoryPromptTemplates = async () => {
    await this.persistStoryPromptConfig();
  };

  handleAddStoryType = async () => {
    const { newStoryTypeLabel, newStoryTypePrompt, storyTypeDefinitions, storyPromptTemplates } = this.state;
    const trimmedLabel = newStoryTypeLabel.trim();
    const trimmedPrompt = newStoryTypePrompt.trim();

    if (!trimmedLabel || !trimmedPrompt) {
      this.setState({ storyPromptSaveStatus: "Add a label and prompt template before creating a slide type." });
      return;
    }

    const nextType = buildCustomStoryTypeKey(trimmedLabel, storyTypeDefinitions);
    const nextDefinition: SlideTypeDefinition = {
      type: nextType,
      label: trimmedLabel,
      description: "Custom story format saved for this community.",
      example: `"${trimmedLabel}"`,
      icon: "✨",
      isCustom: true,
    };
    const nextStoryTypeDefinitions = [...storyTypeDefinitions, nextDefinition];
    const nextStoryPromptTemplates = {
      ...storyPromptTemplates,
      [nextType]: trimmedPrompt,
    };

    this.setState({
      storyTypeDefinitions: nextStoryTypeDefinitions,
      storyPromptTemplates: nextStoryPromptTemplates,
      storyType: nextType,
      selectedPromptStoryType: nextType,
      isAddingStoryType: false,
      newStoryTypeLabel: "",
      newStoryTypePrompt: "",
      storyPromptSaveStatus: "",
    });

    const saved = await this.persistStoryPromptConfig(
      nextStoryTypeDefinitions,
      nextStoryPromptTemplates,
      `Saved "${trimmedLabel}" as a custom slide type.`
    );

    if (!saved) {
      this.setState({
        isAddingStoryType: true,
        newStoryTypeLabel: trimmedLabel,
        newStoryTypePrompt: trimmedPrompt,
      });
    }
  };

  handleRenameStoryType = async (type: StoryType, newLabel: string) => {
    const { storyTypeDefinitions, storyPromptTemplates } = this.state;
    const nextDefinitions = storyTypeDefinitions.map((def) =>
      def.type === type ? { ...def, label: newLabel, example: `"${newLabel}"` } : def
    );
    this.setState({ storyTypeDefinitions: nextDefinitions });
    await this.persistStoryPromptConfig(nextDefinitions, storyPromptTemplates, `Renamed to "${newLabel}".`);
  };

  handleRemoveStoryType = async (type: StoryType) => {
    const { storyTypeDefinitions, storyPromptTemplates, storyType, selectedPromptStoryType } = this.state;
    const nextDefinitions = storyTypeDefinitions.filter((def) => def.type !== type);
    const nextTemplates = { ...storyPromptTemplates };
    delete nextTemplates[type as string];
    const fallbackType = nextDefinitions[0]?.type ?? "";
    this.setState({
      storyTypeDefinitions: nextDefinitions,
      storyPromptTemplates: nextTemplates,
      storyType: storyType === type ? fallbackType : storyType,
      selectedPromptStoryType: selectedPromptStoryType === type ? fallbackType : selectedPromptStoryType,
    });
    await this.persistStoryPromptConfig(nextDefinitions, nextTemplates, "Custom format removed.");
  };

  handleClear = () =>
    this.setState({ slides: [], progress: "", progressPercent: 0, currentStep: "idle", promptInputs: [], aiGeneratingIndex: null, renderedSlides: [] });

  // ─── Save ──────────────────────────────────────────────────────────────────

  uploadBase64ToStorage = async (base64: string, path: string): Promise<string> => {
    const blob = await (await fetch(base64)).blob();
    return (
      await uploadMedia({
        body: blob,
        kind: "image",
        firebasePath: path,
      })
    ).url;
  };

  saveSlides = async () => {
    const { communityId, stepId, setFieldValue } = this.props;
    const {
      slides,
      writingStyle,
      globalTheme,
      buyerPersona,
      editableStepText,
      selectedAuthorId,
      selectedAuthorName,
      selectedAuthorProfilePhotoURL,
      storyTypeDefinitions,
      storyPromptTemplates,
    } = this.state;
    if (!slides.length) { alert("No slides to save."); return; }

    const slideAuthorReference = selectedAuthorId
      ? {
          id: selectedAuthorId,
          name: selectedAuthorName,
          profilePhotoURL: selectedAuthorProfilePhotoURL,
        }
      : null;

    this.setState({ isSaving: true, progress: "Preparing slides for save..." });
    try {
      const processedSlides: SlideContent[] = [];
      for (let i = 0; i < slides.length; i++) {
        this.setState({ progress: `Processing slide ${i + 1} of ${slides.length}...` });
        const slide = { ...slides[i] };
        const processedOptions: SlideImageOption[] = [];
        for (let j = 0; j < slide.options.length; j++) {
          const opt = { ...slide.options[j] };
          if (opt.imageUrl.startsWith("data:") && communityId && stepId) {
            try {
              const url = await this.uploadBase64ToStorage(opt.imageUrl, `${DB_PATH.VIDEO}/${communityId}/${stepId}/slides/slide-${i}-opt-${j}.png`);
              opt.imageUrl = url; opt.thumbnailUrl = url;
            } catch (e) { console.error(`Failed to upload slide ${i + 1} option ${j + 1}:`, e); }
          }
          processedOptions.push(opt);
        }
        const { referenceImages, ...slideWithoutRefs } = slide;
        processedSlides.push({ ...slideWithoutRefs, options: processedOptions });
      }

      this.setState({ progress: "Saving to database..." });
      if (communityId && stepId) {
        const db = firebase.firestore();
        await db.collection(DB_PATH.PLAN_STEP).doc(stepId).set(
          { slides: processedSlides, slideAuthorReference, text: editableStepText || "" },
          { merge: true }
        );
        await db.collection(DB_PATH.COMMUNITY).doc(communityId).set({
          writingStyle: writingStyle || "",
          slideVisualStyle: globalTheme || "",
          buyerPersona: buyerPersona || "",
        }, { merge: true });
        await saveCommunitySlideSettings(communityId, {
          slideStoryTypes: getCustomStoryTypeDefinitions(storyTypeDefinitions),
          slideStoryPromptTemplates: storyPromptTemplates,
        });
      }

      this.setState({ slides: processedSlides });
      setFieldValue?.("slides", processedSlides);
      setFieldValue?.("slideAuthorReference", slideAuthorReference);
      setFieldValue?.("text", editableStepText || "");
      this.setState({ stepTextSaveStatus: "Step text saved with slides." });
      this.setState({ progress: "Slides saved successfully!", currentStep: "complete", isSaving: false });
    } catch (e) {
      console.error("Error saving slides:", e);
      this.setState({ progress: "Error saving slides. Please try again.", isSaving: false });
    }
  };

  // ─── Download ──────────────────────────────────────────────────────────────

  downloadSlide = async (idx: number) => {
    const { slides } = this.state;
    const slide = slides[idx];
    const selected = slide?.options[slide.selectedIndex];
    if (!selected) return;

    this.setState({ isDownloading: true, progress: `Rendering slide ${idx + 1}...` });
    try {
      const communityName = idx === 0 ? await this.loadCommunityName() : undefined;
      const dataUrl = await renderSlideWithTextOverlay(selected.imageUrl, slide.title, slide.subtitle, idx, slides.length, this.props.primaryColor || "#FFD700", communityName);
      const link = document.createElement("a");
      link.download = `slide-${idx + 1}.png`; link.href = dataUrl; link.click();
      this.setState({ progress: "Download complete!", isDownloading: false });
    } catch (e) {
      console.error("Error downloading slide:", e);
      this.setState({ progress: "Error downloading slide.", isDownloading: false });
    }
  };

  downloadAllSlides = async () => {
    const { slides } = this.state;
    if (!slides.length) return;
    this.setState({ isDownloading: true, progress: "Rendering all slides...", progressPercent: 0 });
    try {
      const communityName = await this.loadCommunityName();
      const renderedSlides: string[] = [];
      for (let i = 0; i < slides.length; i++) {
        const selected = slides[i].options[slides[i].selectedIndex];
        if (!selected) continue;
        this.setState({ progress: `Rendering slide ${i + 1} of ${slides.length}...`, progressPercent: Math.floor(((i + 1) / slides.length) * 80) });
        renderedSlides.push(await renderSlideWithTextOverlay(selected.imageUrl, slides[i].title, slides[i].subtitle, i, slides.length, this.props.primaryColor || "#FFD700", i === 0 ? communityName : undefined));
      }
      this.setState({ renderedSlides, progress: "Downloading slides...", progressPercent: 90 });
      for (let i = 0; i < renderedSlides.length; i++) {
        await new Promise((r) => setTimeout(r, 300));
        const link = document.createElement("a");
        link.download = `slide-${i + 1}.png`; link.href = renderedSlides[i]; link.click();
      }
      this.setState({ progress: `Downloaded ${renderedSlides.length} slides!`, progressPercent: 100, isDownloading: false });
    } catch (e) {
      console.error("Error downloading slides:", e);
      this.setState({ progress: "Error downloading slides.", isDownloading: false });
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  render() {
    const { primaryColor } = this.props;
    const {
      isGenerating,
      isSaving,
      isDownloading,
      progress,
      progressPercent,
      slides,
      currentStep,
      promptInputs,
      imageProvider,
      aiGeneratingAll,
      aiGeneratingAllProgress,
      aiGeneratingIndex,
      renderedSlides,
      storyType,
      editableStepText,
      storyTypeDefinitions,
      authors,
      selectedAuthorId,
      selectedAuthorName,
      selectedAuthorProfilePhotoURL,
    } = this.state;

    return (
        <SlideGeneratorStudioView
          slides={slides}
          promptInputs={promptInputs}
          currentStep={currentStep}
          progress={progress}
          progressPercent={progressPercent}
          isGenerating={isGenerating}
          isSaving={isSaving}
          isDownloading={isDownloading}
          aiGeneratingIndex={aiGeneratingIndex}
          aiGeneratingAll={aiGeneratingAll}
          aiGeneratingAllProgress={aiGeneratingAllProgress}
          renderedSlides={renderedSlides}
          imageProvider={imageProvider}
          storyType={storyType}
          storyTypeDefinitions={storyTypeDefinitions}
          autoPickedStoryType={this.state.autoPickedStoryType}
          autoPickReason={this.state.autoPickReason}
          isPickingStoryFormat={this.state.isPickingStoryFormat}
          authors={authors}
          selectedAuthorId={selectedAuthorId}
          selectedAuthorName={selectedAuthorName}
          selectedAuthorProfilePhotoURL={selectedAuthorProfilePhotoURL}
          editableStepText={editableStepText}
          canSave={!!this.props.communityId && !!this.props.stepId}
          onStoryTypeChange={(t) =>
            this.setState({ storyType: t, selectedPromptStoryType: t, storyPromptSaveStatus: "" })
          }
          onProviderChange={(p) => this.setState({ imageProvider: p })}
          onAuthorChange={this.handleAuthorSelectionChange}
          onGenerate={this.generateSlides}
          onGenerateAll={this.handleGenerateAllAIImages}
          onTitleChange={this.handleTitleChange}
          onSubtitleChange={this.handleSubtitleChange}
          onPromptChange={this.handlePromptInputChange}
          onSelectOption={this.handleSelectOption}
          onGenerateAI={this.handleGenerateAIForSlide}
          onSearchUnsplash={this.handleSearchForSlide}
          onUpload={this.handleUploadForSlide}
          onSave={this.saveSlides}
          onDownloadSlide={this.downloadSlide}
          onDownloadAll={this.downloadAllSlides}
          onClear={this.handleClear}
          primaryColor={primaryColor}
        />
      );
  }
}

export default SlideGenerator;
